// Group G8's words: the meeting event (#98), the responses (#99), the Final Interview Loop sequence (row 103), the panel
// acknowledgment summary (row 105), the Recruiter screen card (row 122), the reference check card (row 123) and the
// own-score revision (#70 .revise). Each entry quotes the line of his glossary or of the vendored design-system
// excerpts it is taken from; labels.test.mjs checks every quote, text and status. Words another module already
// carries (status chains, verbs, the diff heads, the reason box) are imported there, not repeated here.

import { defineLabels, template, type LabelDef, type LabelSource } from "../labels";

const his = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "his" }) as const satisfies LabelDef;

const EVENT_WORDS = "*Outlook event* · *Teams link* · *meeting ID* · *room* · *time zone* · *delivery status* · *Candidate itinerary* · *Reschedule* · *Cancel*";
const EVENT_ROW = "the meeting event: itinerary grid + *Meeting ID* (mono) + *Delivery status*";
const TEAMS = "\"Store meeting ID, Teams URL, attendees, response status, room, time zone and delivery status.\"";
const RESP_ROW = "*Responses* section: `person` rows with *Accepted / Declined / Acknowledgment Pending / nonresponse* pills, the *Coordinator* tag, the *panel-change workflow* link";
const RESP_WORDS = "*response status* · *Accepted* · *Declined* · *Acknowledgment Pending* · *nonresponse*";
const SEQ_WORDS = "*Final Interview Loop* · *virtual / onsite / hybrid* · *presentations* · *assessments* · *executive conversations* · *room* · *travel* · *sequence*";
const TASKS = "\"room/link/travel tasks\"";
const ACKSUM = "*Panel acknowledgment summary*: ring (accepted n of N) + three counts + one row per interviewer (round · assigned chips · response pill · responded · *Panel change* on a Declined row)";
const SCREEN_WORDS = "*Recruiter screen* · *Screen Requested · Scheduled · Completed · Advance · Hold · Pass · Withdrawn* · *First contact* · *Follow-up* · *Screen content* · *Record outcome* · *Withdrawal reason*";
const SCREEN_DATES = "`kv` *First contact · Screen · Follow-up* (dates) · *Screen content* `inp.ta`";
const WITHDRAWAL = "| Withdrawal stage / withdrawal date |";
const SCREEN_CHAIN = "\"Statuses / workflow: Screen Requested -> Scheduled -> Completed -> Advance/Hold/Pass/Withdrawn.\"";
const AUTHORIZED = "\"Recruiter records outcome. Coordinator schedules. HM sees authorized results. Interviewers see nothing until assigned.\"";
const REF_WORDS = "*Reference Check* · *Not Required · Required · Requested · In Progress · Completed · Unable to Complete* · *Owner · Provider · Requested · Completed · Outcome · Notes*";

/** #98 the meeting event: its title word, the stored fields and the copy action's object. */
export const EVENT_LABELS = /* @__PURE__ */ defineLabels({
  outlookEvent: his("Outlook event"),
  meetingId: his("Meeting ID"),
  teamsUrl: his("Teams URL"),
});

/** #99 the Responses section. */
export const RESPONSE_LABELS = /* @__PURE__ */ defineLabels({
  responses: his("Responses"),
  responseStatus: his("response status"),
  panelChangeWorkflow: his("panel-change workflow"),
});

/** Row 103 the Final Interview Loop sequence: the block's name, the round formats and the room and travel task rows. */
export const LOOP_LABELS = /* @__PURE__ */ defineLabels({
  finalInterviewLoop: his("Final Interview Loop"),
  sequence: his("sequence"),
  presentations: his("presentations"),
  assessments: his("assessments"),
  executiveConversations: his("executive conversations"),
  room: his("room"),
  travel: his("travel"),
  link: his("link"),
});

/** Row 105 the panel acknowledgment summary. */
export const ACKSUM_LABELS = /* @__PURE__ */ defineLabels({
  panelAcknowledgmentSummary: his("Panel acknowledgment summary"),
  panelChange: his("Panel change"),
  round: his("round"),
  responded: his("responded"),
});

/** Row 122 the Recruiter screen card: its title, the three dates, the content field, the withdrawal's stage and date. */
export const SCREEN_LABELS = /* @__PURE__ */ defineLabels({
  recruiterScreen: his("Recruiter screen"),
  firstContact: his("First contact"),
  screen: his("Screen"),
  followUp: his("Follow-up"),
  screenContent: his("Screen content"),
  withdrawalStage: his("Withdrawal stage"),
  withdrawalDate: his("Withdrawal date"),
  chain: his("Screen Requested -> Scheduled -> Completed -> Advance/Hold/Pass/Withdrawn."),
  authorizedResults: his("HM sees authorized results."),
});

/** Row 123 the reference check card: its title, the requirement flag, the two date fields and the primary. */
export const REFCHECK_LABELS = /* @__PURE__ */ defineLabels({
  referenceCheck: his("Reference Check"),
  requirementFlag: his("Requirement flag"),
  requested: his("Requested"),
  completed: his("Completed"),
  record: his("Record"),
});

/** The acknowledgment ring's name (row 105's *accepted n of N*). */
export const G8_TEMPLATES = {
  acceptedOf: /* @__PURE__ */ template(his("accepted {n} of {total}")),
} as const;
